import type { CSSProperties, FC, ReactNode } from 'react'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { FigLabel } from '@/components/generals/fig-label'
import { PageHeader } from '@/components/generals/page-header'
import { PrimaryButton } from '@/components/generals/primary-button'
import { Reveal } from '@/components/generals/reveal'
import { ServiceRows } from '@/components/practice/service-rows'

import { CLIENT_TYPES } from '@/constants/practice'
import { STUDIO_FACTS } from '@/constants/studio'

const delay = (value: number): CSSProperties => ({ '--delay': `${value}ms` } as CSSProperties)

const PracticePage: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Practice'
                description='Five services from one studio: new architecture, retrofit and heritage, survey and digital twins, building performance, and competitions and feasibility.'
                figure='Fig. 01 / Practice / Five services'
                shape='housing'
            />

            <PageHeader
                fig='01'
                title='Practice'
                detail='Five services'
                note='Hover or tap a row to open it'
                lines={['Buildings that', 'keep reporting back.']}
                intro={(
                    <p>
                        We design new buildings, repair old ones and measure both for as long as they stand. Every service runs on the same live model, so a survey today becomes the monitoring of the next decade.
                    </p>
                )}
                readouts={[
                    { label: 'Founded', value: STUDIO_FACTS.founded },
                    { label: 'People', value: STUDIO_FACTS.people },
                    { label: 'Projects', value: STUDIO_FACTS.projects },
                    { label: 'Countries', value: STUDIO_FACTS.countries }
                ]}
            />

            <section aria-labelledby='services-title' className='pt-20 desk:pt-28'>
                <Reveal className='px-gutter pb-12 desk:pb-16'>
                    <FigLabel fig='02' title='Services' detail='One studio, one model' note='Five services / one team / one model per building' />

                    <h2 id='services-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                From a first sketch
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                to the annual audit.
                            </span>
                        </span>
                    </h2>
                </Reveal>

                <ServiceRows />
            </section>

            <section aria-labelledby='clients-title' className='pt-20 desk:pt-28'>
                <Reveal className='px-gutter pb-12 desk:pb-16'>
                    <FigLabel fig='03' title='Who we work with' detail='Clients' note='Owners who keep their buildings for decades' />

                    <h2 id='clients-title' className='display-lg mt-10'>
                        <span className='reveal-line'>
                            <span className='reveal-inner text-ink' style={delay(80)}>
                                Owners who plan
                            </span>
                        </span>

                        <span className='reveal-line'>
                            <span className='reveal-inner text-dim' style={delay(170)}>
                                in decades.
                            </span>
                        </span>
                    </h2>
                </Reveal>

                <Reveal>
                    <ul className='grid gap-px border-y border-hairline bg-hairline sm:grid-cols-2 desk:grid-cols-5'>
                        {CLIENT_TYPES.map((client, index) => (
                            <li key={client.name} className='reveal-fade flex min-h-[200px] flex-col justify-between gap-8 bg-background px-gutter py-8 desk:px-6 desk:first:pl-gutter desk:last:pr-gutter' style={delay(index * 80)}>
                                <span className='hud text-mute'>
                                    C.{String(index + 1).padStart(2, '0')}
                                </span>

                                <span>
                                    <span className='block font-heading text-[22px] font-medium leading-tight tracking-[-0.02em] text-ink'>
                                        {client.name}
                                    </span>

                                    <span className='mt-3 block text-[14px] leading-[1.55] text-ink/70'>
                                        {client.note}
                                    </span>
                                </span>
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </section>

            <section aria-labelledby='practice-next' className='relative'>
                <Reveal className='relative px-gutter pb-24 pt-20 desk:pb-32 desk:pt-28'>
                    <FigLabel fig='04' title='Next step' detail='Engage the studio' note='Replies within two working days' />

                    <div className='mt-10 flex flex-col gap-10 desk:flex-row desk:items-end desk:justify-between'>
                        <h2 id='practice-next' className='display-lg max-w-[18ch]'>
                            <span className='reveal-line'>
                                <span className='reveal-inner text-ink' style={delay(80)}>
                                    Bring us a building,
                                </span>
                            </span>

                            <span className='reveal-line'>
                                <span className='reveal-inner text-dim' style={delay(170)}>
                                    standing or still on paper.
                                </span>
                            </span>
                        </h2>

                        <div className='reveal-fade' style={delay(300)}>
                            <PrimaryButton href='/contact'>
                                Engage the studio
                            </PrimaryButton>
                        </div>
                    </div>
                </Reveal>
            </section>
        </Layout>
    )
}

export default PracticePage
